import { shapesApi, ShapeInstance } from './api';
import {
  ShapeType,
  ShapeParams,
  isPointInShape,
  distanceToShapeStroke,
  calculateShapeBounds
} from '../types/shape';

/**
 * Manages shape instances for a map
 * Handles loading, saving, and manipulating shapes with region-based storage
 */
export class ShapeManager {
  private mapId: string;
  private shapes: Map<string, ShapeInstance> = new Map();
  private loadedRegions: Set<string> = new Set();
  private dirtyShapes: Set<string> = new Set();
  private newShapes: Set<string> = new Set();
  private deletedShapes: Set<string> = new Set();
  private saveTimeout: NodeJS.Timeout | null = null;
  private onShapesChanged: (() => void) | null = null;
  private onSaveStateChanged: ((isSaving: boolean, hasUnsaved: boolean) => void) | null = null;
  private onBeforeSave: (() => Promise<void>) | null = null;
  private isSaving: boolean = false;
  private autoSaveEnabled: boolean = true;
  private nextLocalId: number = 1;

  private static readonly SAVE_DELAY = 2000;
  private static readonly REGION_SIZE = 2048;

  /**
   * Creates a new ShapeManager for a map
   * @param mapId The map ID to manage shapes for
   */
  constructor(mapId: string) {
    this.mapId = mapId;
  }

  /**
   * Set callback for when shapes change
   * @param callback Callback function or null to clear
   */
  setOnShapesChanged(callback: (() => void) | null): void {
    this.onShapesChanged = callback;
  }

  /**
   * Set callback for when save state changes
   * @param callback Callback function or null to clear
   */
  setOnSaveStateChanged(callback: ((isSaving: boolean, hasUnsaved: boolean) => void) | null): void {
    this.onSaveStateChanged = callback;
  }

  /**
   * Set callback to run before saving (e.g., to save layers first)
   * @param callback Callback function or null to clear
   */
  setOnBeforeSave(callback: (() => Promise<void>) | null): void {
    this.onBeforeSave = callback;
  }

  /**
   * Enable or disable autosave
   * @param enabled Whether autosave should be enabled
   */
  setAutoSaveEnabled(enabled: boolean): void {
    this.autoSaveEnabled = enabled;
    if (!enabled && this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }

  /**
   * Notify save state changed
   */
  private notifySaveStateChanged(): void {
    const hasUnsaved = this.dirtyShapes.size > 0 || this.newShapes.size > 0 || this.deletedShapes.size > 0;
    this.onSaveStateChanged?.(this.isSaving, hasUnsaved);
  }

  /**
   * Load all shapes for the map
   */
  async loadAllShapes(): Promise<void> {
    try {
      const response = await shapesApi.list(this.mapId);
      this.shapes.clear();

      for (const shape of response.shapes) {
        this.shapes.set(shape.id, shape);
      }

      this.notifyShapesChanged();
    } catch (err) {
      console.error('Failed to load shapes:', err);
    }
  }

  /**
   * Load shapes for a specific region
   * @param regionX Region X coordinate
   * @param regionY Region Y coordinate
   */
  async loadRegionShapes(regionX: number, regionY: number): Promise<void> {
    const key = `${regionX},${regionY}`;
    if (this.loadedRegions.has(key)) return;

    try {
      const response = await shapesApi.getRegionShapes(this.mapId, regionX, regionY);
      this.loadedRegions.add(key);

      for (const shape of response.shapes) {
        this.shapes.set(shape.id, { ...shape, regionX, regionY });
      }

      this.notifyShapesChanged();
    } catch (err) {
      console.error(`Failed to load shapes for region ${regionX},${regionY}:`, err);
    }
  }

  /**
   * Ensure shapes are loaded for visible regions
   * @param viewportX Viewport center X
   * @param viewportY Viewport center Y
   * @param viewportWidth Viewport width
   * @param viewportHeight Viewport height
   * @param zoom Current zoom level
   */
  async ensureRegionsLoaded(
    viewportX: number,
    viewportY: number,
    viewportWidth: number,
    viewportHeight: number,
    zoom: number
  ): Promise<void> {
    const halfWidth = viewportWidth / (2 * zoom);
    const halfHeight = viewportHeight / (2 * zoom);

    const minX = viewportX - halfWidth;
    const maxX = viewportX + halfWidth;
    const minY = viewportY - halfHeight;
    const maxY = viewportY + halfHeight;

    let minRegionX = Math.floor(minX / ShapeManager.REGION_SIZE);
    let maxRegionX = Math.floor(maxX / ShapeManager.REGION_SIZE);
    let minRegionY = Math.floor(minY / ShapeManager.REGION_SIZE);
    let maxRegionY = Math.floor(maxY / ShapeManager.REGION_SIZE);

    const MAX_REGIONS_PER_AXIS = 10;
    const regionsX = maxRegionX - minRegionX + 1;
    const regionsY = maxRegionY - minRegionY + 1;

    if (regionsX > MAX_REGIONS_PER_AXIS) {
      const centerRegionX = Math.floor(viewportX / ShapeManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionX = centerRegionX - halfRegions;
      maxRegionX = centerRegionX + halfRegions;
    }

    if (regionsY > MAX_REGIONS_PER_AXIS) {
      const centerRegionY = Math.floor(viewportY / ShapeManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionY = centerRegionY - halfRegions;
      maxRegionY = centerRegionY + halfRegions;
    }

    const loadPromises: Promise<void>[] = [];

    for (let rx = minRegionX; rx <= maxRegionX; rx++) {
      for (let ry = minRegionY; ry <= maxRegionY; ry++) {
        loadPromises.push(this.loadRegionShapes(rx, ry));
      }
    }

    await Promise.all(loadPromises);
  }

  /**
   * Get all shapes sorted by zIndex
   * @returns Array of shape instances
   */
  getShapes(): ShapeInstance[] {
    return Array.from(this.shapes.values()).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  }

  /**
   * Get a single shape by ID
   * @param shapeId The shape ID
   * @returns The shape instance or undefined
   */
  getShape(shapeId: string): ShapeInstance | undefined {
    return this.shapes.get(shapeId);
  }

  /**
   * Add a new shape locally
   * @param shapeData Shape data to add
   * @returns The created shape instance
   */
  addShape(shapeData: {
    shapeType: ShapeType;
    shapeParams: ShapeParams;
    x: number;
    y: number;
    rotation?: number;
    scale?: number;
    strokeColor?: string | null;
    strokeWidth?: number;
    strokeOpacity?: number;
    lineCap?: 'butt' | 'round' | 'square';
    lineJoin?: 'miter' | 'round' | 'bevel';
    dashPreset?: 'solid' | 'dashed' | 'dotted';
    fillColor?: string | null;
    fillOpacity?: number;
    featherRadius?: number;
    invertFill?: boolean;
    layerId?: string;
  }): ShapeInstance {
    const localId = `local_shape_${Date.now()}_${this.nextLocalId++}`;

    // Calculate region from center position
    const regionX = Math.floor(shapeData.x / ShapeManager.REGION_SIZE);
    const regionY = Math.floor(shapeData.y / ShapeManager.REGION_SIZE);

    const shape: ShapeInstance = {
      id: localId,
      shapeType: shapeData.shapeType,
      shapeParams: shapeData.shapeParams,
      x: shapeData.x,
      y: shapeData.y,
      rotation: shapeData.rotation ?? 0,
      scale: shapeData.scale ?? 1,
      strokeColor: shapeData.strokeColor ?? '#000000',
      strokeWidth: shapeData.strokeWidth ?? 2,
      strokeOpacity: shapeData.strokeOpacity ?? 1,
      lineCap: shapeData.lineCap ?? 'round',
      lineJoin: shapeData.lineJoin ?? 'round',
      dashPreset: shapeData.dashPreset ?? 'solid',
      fillColor: shapeData.fillColor ?? '#3388ff',
      fillOpacity: shapeData.fillOpacity ?? 0.5,
      featherRadius: shapeData.featherRadius ?? 0,
      invertFill: shapeData.invertFill ?? false,
      zIndex: this.getMaxZIndex() + 1,
      layerId: shapeData.layerId ?? 'default',
      regionX,
      regionY
    };

    this.shapes.set(localId, shape);
    this.newShapes.add(localId);
    this.scheduleSave();
    this.notifyShapesChanged();
    this.notifySaveStateChanged();

    return shape;
  }

  /**
   * Update a shape locally (with debounced save)
   * @param shapeId The shape ID to update
   * @param updates Partial updates to apply
   */
  updateShapeLocal(shapeId: string, updates: Partial<ShapeInstance>): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;

    Object.assign(shape, updates);

    // Recalculate region if position changed
    if (updates.x !== undefined || updates.y !== undefined) {
      shape.regionX = Math.floor(shape.x / ShapeManager.REGION_SIZE);
      shape.regionY = Math.floor(shape.y / ShapeManager.REGION_SIZE);
    }

    this.dirtyShapes.add(shapeId);
    this.scheduleSave();
    this.notifyShapesChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Delete a shape locally
   * @param shapeId The shape ID to delete
   * @returns True if the shape was deleted
   */
  deleteShape(shapeId: string): boolean {
    if (!this.shapes.has(shapeId)) return false;

    this.shapes.delete(shapeId);
    this.dirtyShapes.delete(shapeId);

    if (this.newShapes.has(shapeId)) {
      this.newShapes.delete(shapeId);
    } else {
      this.deletedShapes.add(shapeId);
    }

    this.scheduleSave();
    this.notifyShapesChanged();
    this.notifySaveStateChanged();

    return true;
  }

  /**
   * Move shapes to a different layer
   * @param shapeIds Array of shape IDs to move
   * @param targetLayerId Target layer ID
   */
  moveShapesToLayer(shapeIds: string[], targetLayerId: string): void {
    for (const shapeId of shapeIds) {
      const shape = this.shapes.get(shapeId);
      if (shape) {
        shape.layerId = targetLayerId;
        this.dirtyShapes.add(shapeId);
      }
    }
    this.scheduleSave();
    this.notifyShapesChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Get shapes on a specific layer
   * @param layerId Layer ID to filter by
   * @returns Array of shapes on the layer
   */
  getShapesOnLayer(layerId: string): ShapeInstance[] {
    return this.getShapes().filter(s => (s.layerId || 'default') === layerId);
  }

  /**
   * Delete all shapes on specified layers
   * @param layerIds Array of layer IDs to delete shapes from
   */
  deleteShapesOnLayers(layerIds: string[]): void {
    const layerIdSet = new Set(layerIds);
    const shapesToDelete: string[] = [];

    for (const shape of this.shapes.values()) {
      const shapeLayerId = shape.layerId || 'default';
      if (layerIdSet.has(shapeLayerId)) {
        shapesToDelete.push(shape.id);
      }
    }

    for (const shapeId of shapesToDelete) {
      this.shapes.delete(shapeId);
      this.dirtyShapes.delete(shapeId);

      if (this.newShapes.has(shapeId)) {
        this.newShapes.delete(shapeId);
      } else {
        this.deletedShapes.add(shapeId);
      }
    }

    if (shapesToDelete.length > 0) {
      this.scheduleSave();
      this.notifyShapesChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Find shape at a given world position using hit testing
   * @param worldX World X coordinate
   * @param worldY World Y coordinate
   * @param tolerance Hit tolerance in pixels
   * @param activeLayerId Only find shapes on this layer (or null for all)
   * @returns The shape at the position or null
   */
  findShapeAtPosition(
    worldX: number,
    worldY: number,
    tolerance: number = 10,
    activeLayerId: string | null = null
  ): ShapeInstance | null {
    // Iterate in reverse z-order (top to bottom)
    const sortedShapes = this.getShapes().reverse();

    for (const shape of sortedShapes) {
      // Only find shapes on the active layer if specified
      if (activeLayerId !== null) {
        const shapeLayerId = shape.layerId || 'default';
        if (shapeLayerId !== activeLayerId) continue;
      }

      // Convert API ShapeInstance to internal format for hit testing
      const internalShape = this.toInternalShape(shape);

      // Check if filled and point is inside
      if (shape.fillColor !== null) {
        if (isPointInShape(internalShape, worldX, worldY)) {
          return shape;
        }
      }

      // Check if near the stroke
      if (shape.strokeColor !== null) {
        const dist = distanceToShapeStroke(internalShape, worldX, worldY);
        if (dist <= tolerance + (shape.strokeWidth || 0) / 2) {
          return shape;
        }
      }
    }

    return null;
  }

  /**
   * Find all shapes within a rectangular area
   * @param x1 First corner X
   * @param y1 First corner Y
   * @param x2 Second corner X
   * @param y2 Second corner Y
   * @param activeLayerId Only find shapes on this layer (or null for all)
   * @returns Array of shapes in the rectangle
   */
  findShapesInRect(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    activeLayerId: string | null = null
  ): ShapeInstance[] {
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    const result: ShapeInstance[] = [];

    for (const shape of this.shapes.values()) {
      // Only find shapes on the active layer if specified
      if (activeLayerId !== null) {
        const shapeLayerId = shape.layerId || 'default';
        if (shapeLayerId !== activeLayerId) continue;
      }

      // Get shape bounds
      const internalShape = this.toInternalShape(shape);
      const bounds = calculateShapeBounds(internalShape);

      // Check if shape bounds intersect with selection rect
      const intersects =
        bounds.minX <= maxX &&
        bounds.maxX >= minX &&
        bounds.minY <= maxY &&
        bounds.maxY >= minY;

      if (intersects) {
        result.push(shape);
      }
    }

    return result;
  }

  /**
   * Get the maximum z-index of all shapes
   * @returns Maximum z-index value
   */
  getMaxZIndex(): number {
    let maxZ = 0;
    for (const shape of this.shapes.values()) {
      maxZ = Math.max(maxZ, shape.zIndex || 0);
    }
    return maxZ;
  }

  /**
   * Convert API ShapeInstance to internal shape format for type utilities
   */
  private toInternalShape(shape: ShapeInstance): import('../types/shape').ShapeInstance {
    return {
      id: shape.id,
      shapeType: shape.shapeType,
      shapeParams: shape.shapeParams as ShapeParams,
      x: shape.x,
      y: shape.y,
      rotation: shape.rotation,
      scale: shape.scale,
      strokeColor: shape.strokeColor,
      strokeWidth: shape.strokeWidth,
      strokeOpacity: shape.strokeOpacity,
      lineCap: shape.lineCap,
      lineJoin: shape.lineJoin,
      dashPreset: shape.dashPreset,
      fillColor: shape.fillColor,
      fillOpacity: shape.fillOpacity,
      featherRadius: shape.featherRadius ?? 0,
      invertFill: shape.invertFill ?? false,
      zIndex: shape.zIndex,
      layerId: shape.layerId,
      regionX: shape.regionX,
      regionY: shape.regionY
    };
  }

  /**
   * Schedule a debounced save
   */
  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    if (this.autoSaveEnabled) {
      this.saveTimeout = setTimeout(() => {
        this.saveDirtyShapes();
      }, ShapeManager.SAVE_DELAY);
    }
  }

  /**
   * Save all pending changes
   */
  async saveDirtyShapes(): Promise<void> {
    if (this.dirtyShapes.size === 0 && this.newShapes.size === 0 && this.deletedShapes.size === 0) return;

    // Run before-save callback (e.g., to save layers first and update local IDs)
    if (this.onBeforeSave) {
      await this.onBeforeSave();
    }

    this.isSaving = true;
    this.notifySaveStateChanged();

    try {
      // 1. Delete shapes marked for deletion
      for (const shapeId of this.deletedShapes) {
        try {
          await shapesApi.delete(this.mapId, shapeId);
        } catch (err) {
          console.error(`Failed to delete shape ${shapeId}:`, err);
        }
      }
      this.deletedShapes.clear();

      // 2. Create new shapes
      const idMapping = new Map<string, string>();
      for (const localId of this.newShapes) {
        const shape = this.shapes.get(localId);
        if (shape) {
          try {
            const { id, regionX, regionY, ...shapeData } = shape;
            const response = await shapesApi.create(this.mapId, shapeData as Omit<ShapeInstance, 'id' | 'regionX' | 'regionY'>);

            this.shapes.delete(localId);
            const serverShape = { ...response.shape, regionX: response.regionX, regionY: response.regionY };
            this.shapes.set(serverShape.id, serverShape);
            idMapping.set(localId, serverShape.id);
          } catch (err) {
            console.error(`Failed to create shape:`, err);
          }
        }
      }
      this.newShapes.clear();

      // 3. Update dirty shapes
      for (const shapeId of this.dirtyShapes) {
        const shape = this.shapes.get(shapeId);
        if (shape) {
          try {
            await shapesApi.update(this.mapId, shape.id, shape);
          } catch (err) {
            console.error(`Failed to update shape ${shape.id}:`, err);
          }
        }
      }
      this.dirtyShapes.clear();

      this.notifyShapesChanged();
    } catch (err) {
      console.error('Failed to save shapes:', err);
    } finally {
      this.isSaving = false;
      this.notifySaveStateChanged();
    }
  }

  /**
   * Check if there are unsaved changes
   * @returns True if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.dirtyShapes.size > 0 || this.newShapes.size > 0 || this.deletedShapes.size > 0;
  }

  /**
   * Notify that shapes have changed
   */
  private notifyShapesChanged(): void {
    this.onShapesChanged?.();
  }

  /**
   * Update layer IDs when layers get new server IDs
   * Called when LayerManager saves new layers and gets back server IDs
   * @param idMapping Map of local IDs to server IDs
   */
  updateLayerIds(idMapping: Map<string, string>): void {
    let changed = false;
    for (const shape of this.shapes.values()) {
      if (shape.layerId && idMapping.has(shape.layerId)) {
        shape.layerId = idMapping.get(shape.layerId)!;
        this.dirtyShapes.add(shape.id);
        changed = true;
      }
    }
    if (changed) {
      this.scheduleSave();
      this.notifyShapesChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }
}
